import React, { useEffect, useState } from 'react';
import api from '../../api';
import { teamLogo, fmtCap, CountUp } from './poolFx';

// A player's profile, shown inside the lineup board in "stats mode" (hold a card for 1 second).
// Data: /api/nhl/player/:id (photo, bio, this season / last season / career, last 5 games, cap hit, projection, injury).
const cache = new Map();   // nhlId -> profile (kept while the page is open)
const inflight = new Map(); // nhlId -> the request already on its way
const nhlIdOf = (id) => String(id).split('-').pop();
// Ask for a profile BEFORE it's needed (as soon as a card is pressed or pointed at), so that when it
// opens the numbers are already there — no "loading" state, no jump in height.
export function prefetchProfile(playerId) {
  const id = nhlIdOf(playerId);
  if (cache.has(id)) return Promise.resolve(cache.get(id));
  if (!inflight.has(id)) {
    inflight.set(id, api.fetchNhlPlayer(id)
      .then((d) => { if (d && !d.error) cache.set(id, d); return d && !d.error ? d : null; })
      .catch(() => null)
      .finally(() => inflight.delete(id)));
  }
  return inflight.get(id);
}

const pct = (v) => (v == null ? '—' : Number(v).toFixed(3).replace(/^0/, ''));
const num = (v, d = 0) => (v == null ? '—' : d ? Number(v).toFixed(d) : v);

// compact = in a narrow column (Trades rosters, Free agents side list): only the main stat columns
// quick = you're switching from another player: one short fade instead of the full entrance
export default function PlayerProfile({ pick, poolPoints = null, slot = null, game = null, lang = 'en', onClose, compact = false, quick = false }) {
  const fr = lang === 'fr';
  const nhlId = nhlIdOf(pick.player_id);
  const [p, setP] = useState(cache.get(nhlId) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    if (cache.has(nhlId)) { setP(cache.get(nhlId)); return undefined; }
    setP(null);
    prefetchProfile(nhlId).then((d) => {
      if (!alive) return;
      if (!d) { setFailed(true); return; }
      setP(d);
    });
    return () => { alive = false; };
  }, [nhlId]);

  const goalie = pick.position === 'G';
  // one stat line: this season, last season, career
  const allCols = goalie
    ? [['gp', fr ? 'PJ' : 'GP'], ['w', fr ? 'V' : 'W'], ['l', fr ? 'D' : 'L'], ['so', fr ? 'BL' : 'SO'], ['gaa', fr ? 'MOY' : 'GAA'], ['svp', fr ? '% ARR' : 'SV%']]
    : [['gp', fr ? 'PJ' : 'GP'], ['g', fr ? 'B' : 'G'], ['a', 'A'], ['pts', 'PTS'], ['pm', '+/-'], ['shots', fr ? 'TIRS' : 'SOG'], ['ppp', fr ? 'PTS AN' : 'PPP']];
  const cols = compact ? allCols.filter(([k]) => ['gp', 'g', 'a', 'pts', 'w', 'so', 'svp'].includes(k)) : allCols;
  const cell = (k, v) => (k === 'svp' ? pct(v) : k === 'gaa' ? num(v, 2) : k === 'pm' && v > 0 ? `+${v}` : num(v));
  // the table and the 5 game boxes are ALWAYS drawn (with dashes while the numbers load): the box has its final size from the start
  const rows = [[fr ? 'Cette saison' : 'This season', p?.thisSeason], [fr ? 'Saison dernière' : 'Last season', p?.lastSeason], [fr ? 'Carrière' : 'Career', p?.career]];
  const last5 = p ? p.last5 : [null, null, null, null, null];
  const day = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' });

  return (
    <div className={`pp ${quick ? 'quick' : ''} ${p ? '' : 'loading'}`}>
      <div className="pp-head">
        <span className="pp-photo">
          {p?.headshot ? <img alt="" src={p.headshot} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
            : <img className="pp-photo-logo" alt="" src={teamLogo(pick.team)} />}
        </span>
        <div className="pp-id">
          <span className="pp-name">{pick.player_name}{p?.number != null && <small>#{p.number}</small>}</span>
          <span className="pp-meta">
            <img className="pp-logo" alt="" src={teamLogo(p?.team || pick.team)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
            <b className="dr-slot-pos">{pick.position}</b>
            {[p?.team || pick.team, p?.position && p.position !== pick.position ? p.position : null, p?.age != null ? (fr ? `${p.age} ans` : `${p.age} yrs`) : null, p?.height, p?.weight ? `${p.weight} lb` : null,
              p?.shoots ? (goalie ? (fr ? `attrape ${p.shoots}` : `catches ${p.shoots}`) : (fr ? `lance ${p.shoots}` : `shoots ${p.shoots}`)) : null,
              p?.draft ? (fr ? `repêché ${p.draft.year} · ${p.draft.overall}e` : `drafted ${p.draft.year} · #${p.draft.overall}`) : null].filter(Boolean).join(' · ')}
          </span>
          {p?.injury && <span className="pp-injury">{p.injury.status}{p.injury.text ? ` — ${p.injury.text}` : ''}</span>}
        </div>
        <div className="pp-tiles">
          {poolPoints != null && <span className="pp-tile"><b><CountUp value={poolPoints} duration={500} /></b><small>{fr ? 'pts pool' : 'pool pts'}</small></span>}
          {p?.proj != null && <span className="pp-tile"><b><CountUp value={p.proj} duration={600} /></b><small>{fr ? 'prévision' : 'projection'}</small></span>}
          {(p?.capHit ?? pick.cap_hit) != null && <span className="pp-tile"><b>{fmtCap(Number(p?.capHit ?? pick.cap_hit))}</b><small>{fr ? 'salaire' : 'cap hit'}</small></span>}
          {slot && <span className="pp-tile"><b>{slot}</b><small>{fr ? 'place' : 'spot'}</small></span>}
        </div>
        {onClose && <button type="button" className="pp-close" onClick={onClose} aria-label={fr ? 'Fermer' : 'Close'}>×</button>}
      </div>

      {failed && <div className="pp-wait">{fr ? 'Profil indisponible pour l’instant' : 'Profile not available right now'}</div>}

      {!failed && (
        <div className="pp-body">
          <div className="pp-table">
            <div className="pp-row pp-th" style={{ '--n': cols.length }}><span />{cols.map(([k, l]) => <span key={k}>{l}</span>)}</div>
            {rows.map(([label, r]) => (
              <div key={label} className="pp-row" style={{ '--n': cols.length }}>
                <span className="pp-row-label">{label}</span>
                {cols.map(([k]) => <span key={k} className={k === 'pts' || k === 'w' ? 'strong' : ''}>{r ? cell(k, r[k]) : '—'}</span>)}
              </div>
            ))}
          </div>
          <div className="pp-last">
            <span className="pp-last-title">{fr ? 'Ses 5 derniers matchs' : 'Last 5 games'}{game && !game.final ? <em>{fr ? ' · joue ce soir' : ' · plays tonight'} {game.home ? 'vs' : '@'} {game.opponent}</em> : null}</span>
            {last5.length === 0 && <span className="pp-none">{fr ? 'Aucun match' : 'No games'}</span>}
            <div className="pp-games">
              {last5.map((g, i) => (g ? (
                <span key={g.date + g.opp} className={`pp-game ${goalie ? (g.decision === 'W' ? 'good' : '') : (g.pts > 0 ? 'good' : '')}`}>
                  <small>{day(g.date)} · {g.home ? 'vs' : '@'} {g.opp}</small>
                  <b>{goalie ? `${g.decision || '—'} · ${pct(g.svp)}` : `${g.g}${fr ? 'B' : 'G'} ${g.a}A`}</b>
                </span>
              ) : (
                <span key={i} className="pp-game ghost"><small>&nbsp;</small><b>&nbsp;</b></span>
              )))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
